import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { useLanguage } from '../../Context/LanguageContext';
import { useTheme } from '../../Context/ThemeContext';
import { lightTheme, darkTheme } from '../../Styles/theme';

import FetchModule from '../../Model/Share/Network/FetchModule';

import TotalModalWrapper from '../../Model/Share/Normal/TotalModalWrapper';

import { ReactComponent as DeleteIconSvg } from '../../image/Delete.svg';

export default function SubAccountDeleteModal(props) {
  const { data, onClickExit, onSuccess } = props;

  const history = useHistory();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const theme = isDark ? darkTheme : lightTheme;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const target = data || {};
  const titleTemplate = t('myStoreSetting.account.sub.delete.title');
  const renderedTitle = titleTemplate
    .replace('{name}', target.name || '')
    .replace('{loginId}', target.loginId || '');
  const titleLines = renderedTitle.split('\n');

  function onClickDeleteButton() {
    if (isSubmitting) return;
    if (!target.id) return;

    setIsSubmitting(true);
    const fetchModule = new FetchModule();
    (
      async () => {
        const responseData = await fetchModule.postDataVersion2('sub-account/delete', 'POST', { id: target.id });

        setIsSubmitting(false);

        if (responseData.status === 200) {
          if (onSuccess) onSuccess();
          onClickExit();
        }
        else if (responseData.status === 401) history.push('/');
        else if (responseData.status === 404) alert(t('myStoreSetting.account.sub.alert.notFound'));
        else alert(t('myStoreSetting.alert.serverError'));
      }
    )();
  }

  return (
    <TotalModalWrapper>
      <Modal backgroundColor={theme.subBackground}>
        <IconWrapper backgroundColor={theme.pointRed}>
          <StyledDeleteIcon as={DeleteIconSvg} color={theme.white} />
        </IconWrapper>
        <ExitButtonWrapper onClick={onClickExit}>
          <ExitText color={theme.line}>×</ExitText>
        </ExitButtonWrapper>
        <TextColumn>
          <TitleGroup>
            {titleLines.map((line, idx) => (
              <TitleText key={'DeleteModalTitle-' + idx} color={theme.text}>{line}</TitleText>
            ))}
          </TitleGroup>
          <DescriptionText color={theme.text}>{t('myStoreSetting.account.sub.delete.description')}</DescriptionText>
        </TextColumn>
        <ButtonRow>
          <GoBackButton backgroundColor={theme.subKey} onClick={onClickExit}>
            <ButtonText color={theme.subBackground}>{t('myStoreSetting.account.sub.delete.goBack')}</ButtonText>
          </GoBackButton>
          <DeleteButton backgroundColor={theme.buttonFill3} hoverColor={theme.pointRed} onClick={onClickDeleteButton}>
            <ButtonText color={theme.text}>{t('myStoreSetting.account.sub.delete.delete')}</ButtonText>
          </DeleteButton>
        </ButtonRow>
      </Modal>
    </TotalModalWrapper>
  );
}

const Modal = styled.div`
  position: relative;

  display: flex;
  flex-direction: column;

  justify-content: flex-start;
  align-items: center;

  width: 354px;
  padding: 60px 20px 0 20px;
  box-sizing: border-box;

  border-radius: 15px;
  background-color: ${props => props.backgroundColor};
  box-shadow: 0px 40px 50px rgba(0, 0, 0, 0.1);
`;

const IconWrapper = styled.div`
  position: absolute;
  top: -43px;
  left: 50%;
  transform: translateX(-50%);

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 86px;
  height: 86px;

  border-radius: 1000px;
  background-color: ${props => props.backgroundColor};
`;

const StyledDeleteIcon = styled.svg`
  width: 53px;
  height: 53px;

  & path {
    fill: ${props => props.color};
  }
`;

const ExitButtonWrapper = styled.div`
  position: absolute;
  top: 12px;
  right: 16px;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const ExitText = styled.p`
  font-family: NotoSansKR-Regular;
  font-size: 20px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  line-height: 1;
`;

const TextColumn = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  gap: 20px;

  width: 100%;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;
`;

const TitleText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 16px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  text-align: center;
`;

const DescriptionText = styled.p`
  font-family: NotoSansKR-Medium;
  font-size: 12px;

  color: ${props => props.color};

  margin: 0 0 0 0;

  text-align: center;
`;

const ButtonRow = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: stretch;
  align-items: center;

  height: 52px;

  overflow: hidden;

  border-bottom-left-radius: 15px;
  border-bottom-right-radius: 15px;

  margin: 28px -20px 0 -20px;
  width: calc(100% + 40px);
`;

const GoBackButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  height: 100%;

  background-color: ${props => props.backgroundColor};

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
    }
  }
`;

const DeleteButton = styled.div`
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  flex: 1 0 0;
  height: 100%;

  background-color: ${props => props.backgroundColor};

  transition: background-color 0.15s ease-in-out;

  @media (hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.hoverColor};
    }
  }
`;

const ButtonText = styled.p`
  font-family: NotoSansKR-Bold;
  font-size: 15px;

  color: ${props => props.color};

  margin: 0 0 0 0;
  white-space: nowrap;
`;
